import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import BlogService from "../../../api/services/BlogService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import Footer from "../../../customComponents/Footer";
import "./blog.css";

const PAGE_LIMIT = 12;

const formatBlogDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const firstCategory = (categories) => (Array.isArray(categories) && categories[0] ? categories[0] : null);

const BlogList = () => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [posts, setPosts] = useState([]);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ page: 1, limit: PAGE_LIMIT, total: 0, total_pages: 0 });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [page, selectedCategory]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const result = await BlogService.getBlogCategories();
                if (cancelled) return;
                if (result?.success && Array.isArray(result.data)) {
                    setCategories(result.data);
                } else {
                    setCategories([]);
                }
            } catch {
                if (!cancelled) setCategories([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            LoaderHelper.loaderStatus(true);
            try {
                const result = await BlogService.getBlogs({
                    page,
                    limit: PAGE_LIMIT,
                    category: selectedCategory,
                });
                if (cancelled) return;
                if (result?.success) {
                    setPosts(Array.isArray(result.data) ? result.data : []);
                    setPagination({
                        page: result.pagination?.page || page,
                        limit: result.pagination?.limit || PAGE_LIMIT,
                        total: result.pagination?.total || 0,
                        total_pages: result.pagination?.total_pages || 0,
                    });
                } else {
                    setPosts([]);
                    setPagination({ page: 1, limit: PAGE_LIMIT, total: 0, total_pages: 0 });
                }
            } catch {
                if (!cancelled) {
                    setPosts([]);
                    setPagination({ page: 1, limit: PAGE_LIMIT, total: 0, total_pages: 0 });
                }
            } finally {
                if (!cancelled) LoaderHelper.loaderStatus(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [page, selectedCategory]);

    const featuredPost = posts[0];
    const gridPosts = posts.slice(1);
    const totalPages = pagination.total_pages || 0;

    const openBlogDetails = (slug) => {
        if (!slug) return;
        navigate(`/blogs/${slug}`);
    };

    const selectCategory = (slug) => {
        setSelectedCategory(slug);
        setPage(1);
    };

    return (
        <>
            <div className="blog-page">
                <Helmet>
                    <title>Blog | Crypto Insights & Market Updates – AGCX </title>
                    <meta name="description" content="Read the latest crypto market insights, trading guides, and platform updates from the AGCX team, for UAE and global investors." />
                    <meta
                        name="keywords"
                        content="crypto news UAE, crypto market insights, AGCX blog "
                    />
                    <link rel="canonical" href="https://arabglobal.ae/blogs" />
                </Helmet>
                <div className="blog-container">
                    <div className="blog-content-layout">
                        <div className="blog-main-column">
                            <div className="blog-header">
                                <h1>AGCX Blog</h1>
                                <p>World-Leading Platform of Crypto Information</p>
                                <div className="blog-categories">
                                    <button
                                        type="button"
                                        className={`blog-cat-btn ${selectedCategory === "" ? "active" : ""}`}
                                        onClick={() => selectCategory("")}
                                    >
                                        All
                                    </button>
                                    {categories.map((cat) => (
                                        <button
                                            type="button"
                                            key={cat._id || cat.slug}
                                            className={`blog-cat-btn ${selectedCategory === cat.slug || selectedCategory === cat._id ? "active" : ""}`}
                                            onClick={() => selectCategory(cat.slug || cat._id)}
                                        >
                                            {cat.name}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {featuredPost && (
                                <div
                                    className="featured-post-card"
                                    onClick={() => openBlogDetails(featuredPost.slug)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className="featured-img-container">
                                        <img
                                            src={featuredPost.banner_url || "/images/blog-img/blog_main_img.jpg"}
                                            alt={featuredPost.title}
                                        />
                                    </div>
                                    <div className="featured-post-body">
                                        <div className="post-meta">
                                            {firstCategory(featuredPost.categories) && (
                                                <span
                                                    className="badge-tag"
                                                    style={
                                                        firstCategory(featuredPost.categories).color
                                                            ? { color: firstCategory(featuredPost.categories).color }
                                                            : undefined
                                                    }
                                                >
                                                    {firstCategory(featuredPost.categories).name}
                                                </span>
                                            )}
                                            {featuredPost.read_time_minutes != null && (
                                                <span>🕒 {featuredPost.read_time_minutes} min read</span>
                                            )}
                                            <span>{formatBlogDate(featuredPost.published_at)}</span>
                                        </div>
                                        <h2 className="featured-post-title">{featuredPost.title}</h2>
                                        <p className="featured-post-excerpt">
                                            {featuredPost.excerpt || featuredPost.subtitle}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {posts.length === 0 && (
                                <p className="blog-empty">No blogs found.</p>
                            )}

                            <div className="posts-grid">
                                {gridPosts.map((post) => (
                                    <div
                                        key={post.slug}
                                        className="post-card"
                                        onClick={() => openBlogDetails(post.slug)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <img
                                            src={post.banner_url || "/images/blog-img/blog_main_img.jpg"}
                                            alt={post.title}
                                            className="post-card-img"
                                        />
                                        <div className="post-card-body">
                                            <div className="post-meta">
                                                {firstCategory(post.categories) && (
                                                    <span className="badge-tag">{firstCategory(post.categories).name}</span>
                                                )}
                                                {post.read_time_minutes != null && (
                                                    <span>🕒 {post.read_time_minutes} min read</span>
                                                )}
                                            </div>
                                            <h3 className="post-card-title">{post.title}</h3>
                                            <p className="post-card-desc">{post.excerpt || post.subtitle}</p>
                                            <div className="post-card-footer">
                                                <span>{formatBlogDate(post.published_at)}</span>
                                                <a
                                                    href={`/blogs/${post.slug}`}
                                                    className="read-more-link"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        openBlogDetails(post.slug);
                                                    }}
                                                >
                                                    Read &gt;
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {totalPages > 1 && (
                                <div className="blog-pagination">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                                        <button
                                            type="button"
                                            key={n}
                                            className={`page-btn ${page === n ? "active" : ""}`}
                                            onClick={() => setPage(n)}
                                        >
                                            {n}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        className="page-btn"
                                        disabled={page >= totalPages}
                                        onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                                    >
                                        &gt;
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="blog-sidebar-column">
                            <div className="sidebar-card">
                                <h3 className="sidebar-title">Hot &amp; Trending</h3>
                                <div className="trending-list">
                                    {posts.map((post, idx) => (
                                        <div
                                            key={post.slug}
                                            className="trending-item"
                                            onClick={() => openBlogDetails(post.slug)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <span className="trending-num">{idx + 1}</span>
                                            <div className="trending-info">
                                                <h4 className="trending-headline">{post.title}</h4>
                                                <span className="trending-date">{formatBlogDate(post.published_at)}</span>
                                            </div>
                                        </div>
                                    ))}
                                    {posts.length === 0 && <p className="blog-empty blog-empty--sidebar">No blogs yet.</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default BlogList;
